import { AlertCircle, ChevronDown } from "lucide-react";
import { forwardRef, type SelectHTMLAttributes, useId } from "react";

interface SelectOption {
	value: string;
	label: string;
	disabled?: boolean;
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
	label?: string;
	error?: string;
	hint?: string;
	options?: SelectOption[];
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
	(
		{
			label,
			error,
			hint,
			options = [],
			id,
			className = "",
			children,
			...props
		},
		ref,
	) => {
		const generatedId = useId();
		const selectId = id || generatedId;

		return (
			<div className="space-y-1.5">
				{label && (
					<label
						htmlFor={selectId}
						className="block font-medium text-sm text-text-secondary"
					>
						{label}
					</label>
				)}

				<div className="relative">
					<select
						ref={ref}
						id={selectId}
						className={`w-full cursor-pointer appearance-none rounded-md border bg-bg-secondary px-3 py-2.5 font-sans text-sm text-text-primary transition-all duration-150 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:cursor-not-allowed disabled:opacity-50 pr-10${error ? "border-bug focus:border-bug focus:ring-bug/50" : "border-border hover:border-border-hover"}
							${className}
						`}
						{...props}
					>
						{children ||
							options.map((option) => (
								<option
									key={option.value}
									value={option.value}
									disabled={option.disabled}
								>
									{option.label}
								</option>
							))}
					</select>

					<div className="-translate-y-1/2 pointer-events-none absolute top-1/2 right-3 text-text-tertiary">
						<ChevronDown className="h-4 w-4" />
					</div>
				</div>

				{error ? (
					<p className="flex items-center gap-1 text-bug text-xs">
						<AlertCircle className="h-3.5 w-3.5" />
						{error}
					</p>
				) : hint ? (
					<p className="text-text-tertiary text-xs">{hint}</p>
				) : null}
			</div>
		);
	},
);

Select.displayName = "Select";
